import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import CannedResponsesPanel from "./CannedResponsesPanel";
import TransferChatDialog from "./TransferChatDialog";
import ChatAttachmentComposer from "./ChatAttachmentComposer";
import { ChatAttachmentComposerController } from "./ChatAttachmentComposerController";
import { isHumanAgentInboxLive } from "@/api/inboxAgentApi";
import { attachmentApi } from "@/api/AttachmentApiClient";
import { applyCannedMergeTags, matchSlashQuery } from "@/lib/cannedReplyUtils";
import { clipboardImageFiles } from "@/lib/clipboardImageFiles";
import { surfaceInboxHttpError } from "@/lib/inboxErrorToast";
import BotMessageAvatar from "@/components/chat/BotMessageAvatar";
import AgentMessageAvatar from "@/components/chat/AgentMessageAvatar";
import ChatMessageBody from "@/components/chat/ChatMessageBody";
import ComposerLinkControl from "@/components/chat/ComposerLinkControl";
import MessageAttachments from "@/components/chat/MessageAttachments";
import { isAgentMessageSender, isBotMessageSender } from "@/lib/chatMessageSenders";
import { formatMessageTime } from "@/lib/formatMessageTime";
import { OpenedChatScrollController } from "@/lib/OpenedChatScrollController";
import { useCannedResponses } from "../hooks/useCannedResponses";
import { useWritingAssistant } from "../hooks/useWritingAssistant";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { fetchTicketEmailSenders } from "@/api/ticketEmailApi";
import { toast } from "sonner";

const TICKET_STATUS_LABELS = {
  open: "Open",
  pending: "Pending",
  on_hold: "On Hold",
  resolved: "Resolved",
};
function ticketStatusLabel(status) {
  return TICKET_STATUS_LABELS[status] ?? String(status ?? "");
}

function formatSenderLabel(sender) {
  const name = (sender?.name || "").trim();
  const email = (sender?.email || "").trim();
  if (name && email) return `${name} <${email}>`;
  return email || name || "Sender";
}

function pickDefaultSenderEmail(senders, messages) {
  if (!Array.isArray(senders) || !senders.length) return "";
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg?.sender !== "agent") continue;
    const prior = (msg.senderEmail || msg.sender_email || "").trim().toLowerCase();
    if (!prior) continue;
    const match = senders.find((s) => (s.email || "").trim().toLowerCase() === prior);
    if (match) return match.email;
  }
  const fallback = senders.find((s) => s.isDefault) || senders[0];
  return fallback?.email || "";
}

export default function ChatThread({
  conversationId,
  conversation,
  messages,
  threadLoading,
  threadError,
  sendMessage,
  deleteMessage,
  onRetryThread,
  selectedTicket,
  submitTicketWithStatus,
  onAfterTicketAction,
  transferChat,
  onAfterTransfer,
  onResolve,
  onReopen,
  onTakeOver,
  activeWorkspaceId = null,
  showReplyNoteTabs = true,
  readOnly = false,
}) {
  const conv = conversation;
  const { activeWorkspace, workspaces } = useWorkspace();
  const workspaceLogoUrl = useMemo(() => {
    const wid =
      conv?.workspaceId != null && String(conv.workspaceId).trim()
        ? String(conv.workspaceId).trim()
        : activeWorkspace?.id ?? null;
    const ws = (wid ? workspaces.find((w) => w.id === wid) : null) ?? activeWorkspace;
    return ws?.logoUrl ?? null;
  }, [conv?.workspaceId, workspaces, activeWorkspace]);
  const isClosed = conv?.status === "resolved";
  const composerLocked = readOnly || isClosed;
  const [transferOpen, setTransferOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [activeTab, setActiveTab] = useState("reply");
  const [sendBusy, setSendBusy] = useState(false);
  const [deleteBusyId, setDeleteBusyId] = useState(null);
  const [, setAttachmentTick] = useState(0);
  const [resolveBusy, setResolveBusy] = useState(false);
  const [takeoverBusy, setTakeoverBusy] = useState(false);
  const [ticketSubmitBusy, setTicketSubmitBusy] = useState(false);
  const [emailSenders, setEmailSenders] = useState([]);
  const [selectedFromEmail, setSelectedFromEmail] = useState("");
  const cannedScope = useMemo(() => ({ workspaceId: activeWorkspaceId }), [activeWorkspaceId]);
  const {
    items: cannedResponses,
    loading: cannedLoading,
    error: cannedError,
    refresh: refreshCannedResponses,
    createCannedResponse,
  } = useCannedResponses(cannedScope);
  const {
    suggestions: aiSuggestions,
    clearSuggestions,
    fetchSuggestions,
    runProofread,
    suggestLoading: aiSuggestLoading,
    proofLoading: aiProofLoading,
    error: aiWritingError,
    clearError: clearAiWritingError,
  } = useWritingAssistant();
  const [slashMenu, setSlashMenu] = useState(null);
  const [showNewMessageChip, setShowNewMessageChip] = useState(false);
  const bottomRef = useRef(null);
  const threadScrollRef = useRef(null);
  const scrollControllerRef = useRef(null);
  if (!scrollControllerRef.current) {
    scrollControllerRef.current = new OpenedChatScrollController();
  }
  const textareaRef = useRef(null);
  const slashActiveItemRef = useRef(null);
  const replyTextRef = useRef(replyText);
  const savedRepliesRef = useRef(null);
  const composerPasteRef = useRef(null);
  const attachControllerRef = useRef(null);
  if (!attachControllerRef.current) {
    attachControllerRef.current = new ChatAttachmentComposerController();
  }
  const attachController = attachControllerRef.current;
  const isEmailTicket = selectedTicket?.source === "email";
  const selectedSender =
    emailSenders.find((s) => (s.email || "").trim() === selectedFromEmail) ||
    emailSenders.find((s) => s.isDefault) ||
    emailSenders[0] ||
    null;

  replyTextRef.current = replyText;

  const threadWorkspaceId = useMemo(() => {
    const fromConv =
      conv?.workspaceId != null && String(conv.workspaceId).trim()
        ? String(conv.workspaceId).trim()
        : null;
    return fromConv ?? activeWorkspaceId ?? null;
  }, [conv?.workspaceId, activeWorkspaceId]);

  useEffect(() => {
    const wid = threadWorkspaceId || activeWorkspaceId;
    if (!wid) return undefined;
    let live = true;
    void fetchTicketEmailSenders({ workspaceId: wid })
      .then((payload) => {
        if (!live) return;
        setEmailSenders(Array.isArray(payload?.senders) ? payload.senders : []);
      })
      .catch(() => {
        if (live) setEmailSenders([]);
      });
    return () => {
      live = false;
    };
  }, [threadWorkspaceId, activeWorkspaceId]);

  useEffect(() => {
    if (!isEmailTicket) {
      setSelectedFromEmail("");
      return;
    }
    setSelectedFromEmail(pickDefaultSenderEmail(emailSenders, messages));
  }, [selectedTicket?.id, isEmailTicket, emailSenders, messages]);

  const fetchLiveDownloadUrl = useCallback(
    (attachmentId) =>
      attachmentApi.fetchDownloadUrl({
        attachmentId,
        workspaceId: threadWorkspaceId,
        sessionId: conversationId,
        context: "live_chat",
      }),
    [threadWorkspaceId, conversationId],
  );

  useEffect(() => {
    setReplyText("");
    setSlashMenu(null);
    setTransferOpen(false);
    clearSuggestions();
    clearAiWritingError();
    attachController.setContext("live_chat");
  }, [conversationId, clearSuggestions, clearAiWritingError, attachController]);

  useEffect(() => {
    const ctrl = scrollControllerRef.current;
    if (!ctrl) return;
    const decision = ctrl.evaluate(conversationId, messages.length);
    if (decision.behavior) {
      bottomRef.current?.scrollIntoView({ behavior: decision.behavior });
    }
    setShowNewMessageChip(decision.showChip);
  }, [conversationId, messages.length]);

  const updateSlashMenu = useCallback(
    (text, cursorPos) => {
      if (activeTab !== "reply") {
        setSlashMenu(null);
        return;
      }
      const m = matchSlashQuery(text, cursorPos);
      if (!m) {
        setSlashMenu(null);
        return;
      }
      const q = m.query;
      const filtered = cannedResponses.filter(
        (cr) => cr.shortcut.startsWith(q) || cr.title.toLowerCase().includes(q)
      );
      setSlashMenu((prev) => {
        // Keep the highlighted row while the same /query stays open so arrow
        // keys survive the selection-driven re-sync.
        const sameQuery =
          prev && prev.match.start === m.start && prev.match.query === m.query;
        const activeIndex = sameQuery
          ? Math.min(prev.activeIndex, Math.max(filtered.length - 1, 0))
          : 0;
        return { match: m, filtered, activeIndex };
      });
    },
    [activeTab, cannedResponses]
  );

  const moveSlashActive = useCallback((step) => {
    setSlashMenu((prev) => {
      if (!prev || prev.filtered.length === 0) return prev;
      const count = prev.filtered.length;
      const activeIndex = (prev.activeIndex + step + count) % count;
      return { ...prev, activeIndex };
    });
  }, []);

  const syncSlashMenuFromTextarea = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    updateSlashMenu(replyTextRef.current, ta.selectionStart);
  }, [updateSlashMenu]);

  useEffect(() => {
    if (activeTab !== "reply") setSlashMenu(null);
  }, [activeTab]);

  useEffect(() => {
    slashActiveItemRef.current?.scrollIntoView({ block: "nearest" });
  }, [slashMenu?.activeIndex, slashMenu?.filtered]);

  useEffect(() => {
    if (!showReplyNoteTabs && activeTab !== "reply") {
      setActiveTab("reply");
    }
  }, [showReplyNoteTabs, activeTab]);

  const insertAtCursor = useCallback((chunk) => {
    const ta = textareaRef.current;
    if (!ta) {
      setReplyText((prev) => prev + chunk);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const value = replyTextRef.current;
    const next = value.slice(0, start) + chunk + value.slice(end);
    setReplyText(next);
    const pos = start + chunk.length;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  }, []);

  const applySlashPick = useCallback(
    (cr) => {
      const ta = textareaRef.current;
      if (!ta || !slashMenu) return;
      const { start } = slashMenu.match;
      const end = ta.selectionStart;
      const merged = applyCannedMergeTags(cr.body, conv?.user);
      const text = replyTextRef.current;
      const next = text.slice(0, start) + merged + text.slice(end);
      setReplyText(next);
      setSlashMenu(null);
      const pos = start + merged.length;
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(pos, pos);
      });
    },
    [conv?.user, slashMenu]
  );

  const handleAttachFiles = (fileList) => {
    if (activeTab === "note") return;
    for (const file of Array.from(fileList || [])) {
      const v = attachController.validateFile(file);
      if (!v.ok) {
        surfaceInboxHttpError(new Error(v.error));
        continue;
      }
      attachController.addLocalFile(file);
    }
    setAttachmentTick((n) => n + 1);
  };

  const handleComposerPaste = (e) => {
    const attachmentsDisabled =
      composerLocked || sendBusy || ticketSubmitBusy || !conversationId || activeTab !== "reply";
    if (attachmentsDisabled) return false;
    const images = clipboardImageFiles(e.clipboardData);
    if (!images.length) return false;
    e.preventDefault();
    handleAttachFiles(images);
    return true;
  };
  composerPasteRef.current = handleComposerPaste;

  // Paste an image without focusing the composer first, as long as the paste is
  // not aimed at another field (search boxes, dialogs, rich text editors).
  useEffect(() => {
    const onWindowPaste = (e) => {
      const target = e.target;
      if (target === textareaRef.current) return;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      ) {
        return;
      }
      if (composerPasteRef.current?.(e)) textareaRef.current?.focus();
    };
    window.addEventListener("paste", onWindowPaste);
    return () => window.removeEventListener("paste", onWindowPaste);
  }, []);

  const handleSend = async () => {
    if (composerLocked || !attachController.canSend(replyText) || sendBusy || ticketSubmitBusy) return;
    if (activeTab === "note" && attachController.pending.length) return;
    setSendBusy(true);
    try {
      let attachmentIds = [];
      if (activeTab === "reply" && isHumanAgentInboxLive() && conversationId) {
        attachmentIds = await attachController.uploadAllPending(attachmentApi, {
          sessionId: conversationId,
          workspaceId: threadWorkspaceId,
        });
      }
      const meta = attachController.metadataForOptimistic();
      if (
        selectedTicket &&
        activeTab === "reply" &&
        submitTicketWithStatus
      ) {
        await submitTicketWithStatus({
          ticketId: selectedTicket.id,
          conversationId,
          messageText: replyText,
          senderTab: "reply",
          newStatus: selectedTicket.status,
          attachmentIds,
          source: selectedTicket.source || (isEmailTicket ? "email" : undefined),
          requesterEmail: selectedTicket.requesterEmail,
          fromEmail: selectedSender?.email,
          fromName: selectedSender?.name,
          subject: selectedTicket.subject,
        });
        onAfterTicketAction?.();
      } else if (sendMessage) {
        await sendMessage(replyText, activeTab, {
          attachmentIds,
          attachments: meta.length ? meta : undefined,
        });
      }
      attachController.clear();
      setReplyText("");
      setSlashMenu(null);
    } catch (e) {
      surfaceInboxHttpError(e);
    } finally {
      setSendBusy(false);
    }
  };

  const handleDeleteMessage = async (message) => {
    if (!message?.canDelete || !deleteMessage || deleteBusyId) return;
    const confirmed = window.confirm(
      "Delete this message for the customer? Workspace admins will retain an audit copy.",
    );
    if (!confirmed) return;
    setDeleteBusyId(message.id);
    try {
      await deleteMessage(message);
      toast.success("Message deleted for the customer.");
    } catch {
      // The thread hook surfaces the authorization/conflict error.
    } finally {
      setDeleteBusyId(null);
    }
  };

  const writingWorkspaceId = threadWorkspaceId;

  const handleAiSuggest = async () => {
    clearAiWritingError();
    await fetchSuggestions({
      messages,
      draftText: replyText,
      ticketSubject: selectedTicket?.subject,
      ticketSummary: selectedTicket?.conversationSummary,
      workspaceId: writingWorkspaceId,
    });
  };

  const handleProofread = async () => {
    const next = await runProofread(replyText, writingWorkspaceId);
    setReplyText(next);
    requestAnimationFrame(() => syncSlashMenuFromTextarea());
  };

  const handleResolve = async () => {
    if (!onResolve || resolveBusy) return;
    setResolveBusy(true);
    try {
      await onResolve();
    } catch (e) {
      surfaceInboxHttpError(e);
      toast.error(e instanceof Error ? e.message : "Could not resolve conversation.");
    } finally {
      setResolveBusy(false);
    }
  };

  const handleReopen = async () => {
    if (!onReopen || resolveBusy) return;
    setResolveBusy(true);
    try {
      await onReopen();
    } catch {
      /* useConversationThread surfaces API errors */
    } finally {
      setResolveBusy(false);
    }
  };

  const handleTakeOver = async () => {
    if (!onTakeOver || takeoverBusy || isClosed) return;
    const confirmed = window.confirm(
      "Stop the bot and assign this conversation to you? The bot will no longer reply in this session.",
    );
    if (!confirmed) return;
    setTakeoverBusy(true);
    try {
      await onTakeOver();
    } catch (e) {
      surfaceInboxHttpError(e);
      toast.error(e instanceof Error ? e.message : "Could not take over conversation.");
    } finally {
      setTakeoverBusy(false);
    }
  };

  const handleSubmitTicketAs = async (newStatus) => {
    if (!selectedTicket || !submitTicketWithStatus || ticketSubmitBusy) return;
    setTicketSubmitBusy(true);
    try {
      let attachmentIds = [];
      if (activeTab === "reply" && isHumanAgentInboxLive()) {
        attachController.setContext("ticket", selectedTicket.id);
        attachmentIds = await attachController.uploadAllPending(attachmentApi, {
          sessionId: conversationId || "",
          workspaceId: activeWorkspaceId,
        });
      }
      await submitTicketWithStatus({
        ticketId: selectedTicket.id,
        conversationId,
        messageText: replyText,
        senderTab: activeTab,
        newStatus,
        attachmentIds,
        source: activeTab === "reply" ? selectedTicket.source || (isEmailTicket ? "email" : undefined) : undefined,
        requesterEmail: selectedTicket.requesterEmail,
        fromEmail: selectedSender?.email,
        fromName: selectedSender?.name,
        subject: selectedTicket.subject,
      });
      attachController.clear();
      setReplyText("");
      setSlashMenu(null);
      onAfterTicketAction?.();
    } catch (e) {
      surfaceInboxHttpError(e);
    } finally {
      setTicketSubmitBusy(false);
    }
  };

  if (threadLoading && !conv) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-3 bg-white px-6 text-center">
        <div className="h-9 w-9 animate-pulse rounded-full bg-slate-200" />
        <p className="text-sm font-medium text-slate-500">Loading conversation…</p>
      </div>
    );
  }

  if (threadError && !conv) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-3 bg-white px-6 text-center">
        <p className="text-sm text-rose-700">{threadError.message}</p>
        {onRetryThread && (
          <button type="button" className="ui-btn-primary !py-2 !text-xs" onClick={() => onRetryThread()}>
            Retry
          </button>
        )}
      </div>
    );
  }

  if (!conv) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col items-center justify-center bg-white px-6 text-center">
        <p className="text-sm font-medium text-slate-500">Select a conversation from the inbox.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col bg-slate-50">
      <header className="flex shrink-0 items-center justify-between border-b border-slate-200/60 bg-white/80 px-5 py-4 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              {conv.identityEmails?.[0] ?? (conv.workspaceName ? `User from ${conv.workspaceName}` : "Visitor")}
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {readOnly && onTakeOver && !isClosed && (
            <button
              type="button"
              onClick={() => void handleTakeOver()}
              disabled={takeoverBusy}
              className="mr-1 inline-flex items-center gap-1.5 rounded-xl bg-teal-600 px-3 py-2 text-xs font-semibold text-white shadow-lg shadow-teal-600/20 transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className="ri-user-received-2-line text-base" aria-hidden />
              {takeoverBusy ? "Taking over…" : "Stop bot & take over"}
            </button>
          )}
          {!readOnly && transferChat && (
            <button
              type="button"
              onClick={() => setTransferOpen(true)}
              disabled={composerLocked}
              className="mr-1 hidden items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:border-teal-300 hover:text-teal-800 disabled:cursor-not-allowed disabled:opacity-50 sm:inline-flex"
            >
              <i className="ri-shuffle-line text-base" aria-hidden />
              Transfer
            </button>
          )}
          {!readOnly && transferChat && (
            <button
              type="button"
              onClick={() => setTransferOpen(true)}
              disabled={composerLocked}
              className="ui-btn-icon sm:hidden disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Transfer conversation"
            >
              <i className="ri-shuffle-line text-lg" />
            </button>
          )}
          {(onResolve || onReopen) && (
          <button
            type="button"
            disabled={resolveBusy}
            onClick={() => void (isClosed ? handleReopen() : handleResolve())}
            className={`ml-1 inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 ${
              isClosed
                ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200/80 hover:bg-emerald-100 focus-visible:ring-emerald-400"
                : "bg-slate-900 text-white shadow-lg shadow-slate-900/20 hover:bg-slate-800 focus-visible:ring-teal-400"
            }`}
          >
            <i className={isClosed ? "ri-lock-unlock-line" : "ri-lock-line"} />
            {resolveBusy ? "…" : isClosed ? "Reopen" : "Resolve"}
          </button>
          )}
        </div>
      </header>

      {!readOnly && transferChat && (
        <TransferChatDialog
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          currentAssignee={conv.assignee}
          conversationId={conversationId}
          workspaceId={activeWorkspaceId}
          transferChat={transferChat}
          onAfterTransfer={onAfterTransfer}
        />
      )}

      {selectedTicket && (
        <div className="shrink-0 border-b border-teal-200 bg-teal-50 px-4 py-2.5 sm:px-5">
          <p className="text-xs font-semibold text-teal-950">
            <i className="ri-ticket-2-line mr-1.5 align-text-bottom text-teal-600" aria-hidden />
            Ticket <span className="font-mono">#{selectedTicket.id}</span>
            <span className="font-normal text-teal-700"> · {ticketStatusLabel(selectedTicket.status)}</span>
          </p>
          <p className="mt-0.5 text-[10px] font-medium leading-relaxed text-teal-800/75">
            Use the actions below to post your draft and move this ticket into the right queue. Status-only updates are allowed with an empty message.
          </p>
        </div>
      )}

      <div
        ref={threadScrollRef}
        className="scrollbar-thin relative min-w-0 flex-1 space-y-4 overflow-x-hidden overflow-y-auto px-4 py-6 sm:px-6"
        onScroll={() => {
          const ctrl = scrollControllerRef.current;
          if (!ctrl) return;
          ctrl.recordScrollPosition(threadScrollRef.current);
          setShowNewMessageChip(ctrl.showChip);
        }}
      >
        {messages.map((msg) => {
          if (msg.type === "system") {
            return (
              <div key={msg.id} className="flex items-center gap-3 py-1">
                <div className="h-px flex-1 bg-slate-200" />
                <div className="flex max-w-[90%] items-center gap-2 rounded-full bg-slate-100/90 px-3 py-1.5 text-xs font-medium text-slate-500 ring-1 ring-slate-200/60">
                  <i className="ri-information-line text-sm text-teal-500" aria-hidden />
                  <span className="leading-snug">{msg.text}</span>
                </div>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
            );
          }

          if (msg.sender === "user") {
            const hideUserText = msg.attachments?.length && (msg.text || "").trim() === "[Attachment]";
            return (
              <div key={msg.id} className="flex min-w-0 items-end gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 ring-2 ring-white shadow-md">
                  <i className="ri-user-3-line text-sm text-slate-500" aria-hidden />
                </div>
                <div className="chat-message-column min-w-0 max-w-[min(72%,520px)]">
                  {!hideUserText ? (
                    <div className="chat-message-bubble rounded-2xl rounded-bl-md border border-slate-200/80 bg-white px-4 py-3 text-sm leading-relaxed text-slate-800 shadow-sm">
                      <ChatMessageBody text={msg.text} />
                    </div>
                  ) : null}
                  {msg.attachments?.length ? (
                    <MessageAttachments
                      attachments={msg.attachments}
                      fetchDownloadUrl={fetchLiveDownloadUrl}
                      variant="user"
                    />
                  ) : null}
                  <p className="mt-1.5 pl-1 text-[11px] font-medium text-slate-400">{formatMessageTime(msg)}</p>
                </div>
              </div>
            );
          }

          if (isBotMessageSender(msg.sender)) {
            const hideBotText = msg.attachments?.length && (msg.text || "").trim() === "[Attachment]";
            return (
              <div key={msg.id} className="flex min-w-0 items-end gap-2.5">
                <BotMessageAvatar />
                <div className="chat-message-column min-w-0 max-w-[min(72%,520px)]">
                  {!hideBotText ? (
                    <div className="chat-message-bubble rounded-2xl rounded-bl-md border border-violet-200/80 bg-violet-50/90 px-4 py-3 text-sm leading-relaxed text-slate-800 shadow-sm dark:border-violet-800/50 dark:bg-violet-950/40 dark:text-slate-100">
                      <ChatMessageBody text={msg.text} />
                    </div>
                  ) : null}
                  {msg.attachments?.length ? (
                    <MessageAttachments
                      attachments={msg.attachments}
                      fetchDownloadUrl={fetchLiveDownloadUrl}
                      variant="user"
                    />
                  ) : null}
                  <p className="mt-1.5 pl-1 text-[11px] font-medium text-slate-400">{formatMessageTime(msg)}</p>
                </div>
              </div>
            );
          }

          if (msg.sender === "note") {
            return (
              <div key={msg.id} className="flex min-w-0 items-end justify-end gap-2.5">
                <div className="chat-message-column min-w-0 max-w-[min(72%,520px)]">
                  <div className="chat-message-bubble rounded-2xl rounded-br-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-950 shadow-sm ring-1 ring-amber-100">
                    <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-700/90">
                      <i className="ri-sticky-note-line" aria-hidden />
                      Internal note
                    </div>
                    <ChatMessageBody text={msg.text} />
                  </div>
                  <p className="mt-1.5 pr-1 text-right text-[11px] font-medium text-slate-400">{formatMessageTime(msg)}</p>
                </div>
                <AgentMessageAvatar variant="note" logoUrl={workspaceLogoUrl} />
              </div>
            );
          }

          if (isAgentMessageSender(msg.sender)) {
            const hideAgentText = msg.attachments?.length && (msg.text || "").trim() === "[Attachment]";
            if (msg.deleted) {
              return (
                <div key={msg.id} className="flex min-w-0 items-end justify-end gap-2.5">
                  <div className="chat-message-column min-w-0 max-w-[min(72%,520px)]">
                    <div className="chat-message-bubble rounded-2xl rounded-br-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-relaxed text-slate-700 shadow-sm dark:border-rose-800/60 dark:bg-rose-950/40 dark:text-slate-100">
                      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-rose-700 dark:text-rose-300">
                        <i className="ri-delete-bin-line" aria-hidden />
                        Deleted message — admin audit copy
                      </div>
                      <ChatMessageBody
                        text={msg.text}
                        className="[&_.chat-md-p]:text-slate-700 [&_.chat-md-list]:text-slate-700 [&_strong]:text-slate-800 dark:[&_.chat-md-p]:text-slate-100 dark:[&_.chat-md-list]:text-slate-100 dark:[&_strong]:text-white"
                      />
                    </div>
                    <p className="mt-1.5 pr-1 text-right text-[11px] font-medium text-slate-400">
                      Deleted by {msg.deletedBy || `agent ${msg.deletedByAgentId || "unknown"}`} ·{" "}
                      {msg.deletedAt ? formatMessageTime({ sentAt: msg.deletedAt }) : "time unavailable"}
                    </p>
                  </div>
                  <AgentMessageAvatar logoUrl={workspaceLogoUrl} />
                </div>
              );
            }
            return (
              <div key={msg.id} className="group flex min-w-0 items-end justify-end gap-2.5">
                {msg.canDelete ? (
                  <button
                    type="button"
                    onClick={() => void handleDeleteMessage(msg)}
                    disabled={deleteBusyId === msg.id}
                    className="mb-6 inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-500 shadow-sm transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-wait disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-rose-800 dark:hover:bg-rose-950/40 dark:hover:text-rose-300"
                    aria-label="Delete last sent message"
                    title="Delete last sent message"
                  >
                    <i
                      className={
                        deleteBusyId === msg.id
                          ? "ri-loader-4-line animate-spin"
                          : "ri-delete-bin-line"
                      }
                      aria-hidden
                    />
                    Delete
                  </button>
                ) : null}
                <div className="chat-message-column min-w-0 max-w-[min(72%,520px)]">
                  {!hideAgentText ? (
                    <div className="chat-message-bubble rounded-2xl rounded-br-md bg-teal-700 px-4 py-3 text-sm leading-relaxed text-white shadow-md [&_.chat-md-p]:text-white [&_.chat-md-list]:text-white [&_strong]:text-white [&_a.chat-md-link]:text-blue-300 [&_a.chat-md-link]:underline [&_a.chat-md-link]:underline-offset-2 [&_a.chat-md-link:hover]:text-blue-200">
                      <ChatMessageBody text={msg.text} />
                    </div>
                  ) : null}
                  {msg.attachments?.length ? (
                    <MessageAttachments
                      attachments={msg.attachments}
                      fetchDownloadUrl={fetchLiveDownloadUrl}
                      variant="agent"
                    />
                  ) : null}
                  <p className="mt-1.5 pr-1 text-right text-[11px] font-medium text-slate-400">{formatMessageTime(msg)}</p>
                </div>
                <AgentMessageAvatar logoUrl={workspaceLogoUrl} />
              </div>
            );
          }

          return null;
        })}
        <div ref={bottomRef} />
        {showNewMessageChip ? (
          <button
            type="button"
            className="sticky bottom-2 left-1/2 z-10 -translate-x-1/2 rounded-full bg-teal-700 px-3 py-1.5 text-xs font-semibold text-white shadow-md"
            onClick={() => {
              const ctrl = scrollControllerRef.current;
              ctrl?.dismissChip();
              setShowNewMessageChip(false);
              bottomRef.current?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            New message
          </button>
        ) : null}
      </div>

      <footer className="shrink-0 border-t border-slate-200/60 bg-white/90 p-4 backdrop-blur-md sm:p-5">
        {composerLocked && (
          <div
            className="mb-4 flex items-start gap-2 rounded-2xl border border-slate-200/80 bg-slate-100/90 px-3 py-2.5 text-xs leading-relaxed text-slate-600 ring-1 ring-slate-900/[0.03] dark:border-slate-700/60 dark:bg-slate-800/60 dark:text-slate-300"
            role="status"
          >
            <i className="ri-lock-line mt-0.5 shrink-0 text-sm text-slate-500" aria-hidden />
            <span>
              {readOnly
                ? isClosed
                  ? "This bot conversation is resolved. Reopen it if you need to review it as open again."
                  : "Bot Messages are view-only until you stop the bot and take over this conversation."
                : "This conversation is resolved. Reopen it to send replies, notes, or attachments."}
            </span>
          </div>
        )}

        {!readOnly && selectedTicket && submitTicketWithStatus && (
          <div className="mb-4 rounded-2xl border border-slate-200/80 bg-slate-50/80 p-3 ring-1 ring-slate-900/[0.03]">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">Ticket response</p>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <button
                type="button"
                disabled={ticketSubmitBusy}
                onClick={() => void handleSubmitTicketAs("pending")}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-amber-200/90 bg-amber-50/90 px-3 py-2.5 text-xs font-semibold text-amber-950 shadow-sm ring-1 ring-amber-100/80 transition hover:bg-amber-100 disabled:opacity-45 sm:min-w-[140px] sm:flex-none"
              >
                <i className="ri-time-line text-base" aria-hidden />
                Submit as pending
              </button>
              <button
                type="button"
                disabled={ticketSubmitBusy}
                onClick={() => void handleSubmitTicketAs("on_hold")}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-sky-200/90 bg-sky-50/90 px-3 py-2.5 text-xs font-semibold text-sky-950 shadow-sm ring-1 ring-sky-100/80 transition hover:bg-sky-100 disabled:opacity-45 sm:min-w-[140px] sm:flex-none"
              >
                <i className="ri-pause-circle-line text-base" aria-hidden />
                Submit as on hold
              </button>
              <button
                type="button"
                disabled={ticketSubmitBusy}
                onClick={() => void handleSubmitTicketAs("resolved")}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-emerald-200/90 bg-emerald-50/90 px-3 py-2.5 text-xs font-semibold text-emerald-950 shadow-sm ring-1 ring-emerald-100/80 transition hover:bg-emerald-100 disabled:opacity-45 sm:min-w-[140px] sm:flex-none"
              >
                <i className="ri-checkbox-circle-line text-base" aria-hidden />
                Submit as resolved
              </button>
            </div>
          </div>
        )}

        {!readOnly && (
          <>
        {showReplyNoteTabs && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setActiveTab("reply")}
              disabled={composerLocked}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                activeTab === "reply" ? "bg-slate-900 text-white shadow-md" : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              <i className="ri-reply-line" aria-hidden />
              Reply
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("note")}
              disabled={composerLocked}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                activeTab === "note" ? "bg-amber-100 text-amber-900 ring-1 ring-amber-200/80" : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              <i className="ri-sticky-note-line" aria-hidden />
              Note
            </button>
            {activeTab === "reply" ? (
              <ChatAttachmentComposer
                layout="inlineButton"
                disabled={composerLocked || sendBusy || ticketSubmitBusy || !conversationId}
                controller={attachController}
                onFilesSelected={handleAttachFiles}
              />
            ) : null}
          </div>
        )}

        {aiWritingError && (
          <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-rose-200 bg-rose-50/90 px-3 py-2 text-xs text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-300" role="alert">
            <span>{aiWritingError.message}</span>
            <button type="button" className="font-semibold underline" onClick={clearAiWritingError}>
              Dismiss
            </button>
          </div>
        )}

        {aiSuggestions.length > 0 && (
          <div className="mb-3 rounded-xl border border-violet-200/80 bg-violet-50/60 px-3 py-2.5 ring-1 ring-violet-500/10 dark:border-violet-800/50 dark:bg-violet-950/30 dark:ring-violet-500/10">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-violet-800 dark:text-violet-300">AI suggested replies</p>
              <button type="button" className="text-[11px] font-semibold text-violet-700 hover:text-violet-900 dark:text-violet-400 dark:hover:text-violet-200" onClick={clearSuggestions}>
                Clear
              </button>
            </div>
            <div className="flex max-h-40 flex-col gap-2 overflow-y-auto">
              {aiSuggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  disabled={composerLocked}
                  onClick={() => {
                    if (composerLocked) return;
                    setReplyText(s);
                    clearSuggestions();
                    requestAnimationFrame(() => textareaRef.current?.focus());
                  }}
                  className="rounded-lg border border-violet-200/90 bg-white px-3 py-2 text-left text-xs leading-snug text-slate-800 shadow-sm transition hover:border-violet-400 hover:bg-violet-50/80 disabled:cursor-not-allowed disabled:opacity-50 dark:border-violet-700/60 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-violet-500 dark:hover:bg-violet-950/40"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === "reply" ? (
          <ChatAttachmentComposer
            layout="pendingOnly"
            disabled={composerLocked || sendBusy || ticketSubmitBusy || !conversationId}
            controller={attachController}
            onFilesSelected={handleAttachFiles}
          />
        ) : null}

        <div
          className={`relative rounded-2xl border border-slate-200/80 bg-slate-50/50 p-3 ring-1 ring-slate-900/[0.02] transition-colors focus-within:border-teal-200 focus-within:bg-white focus-within:ring-teal-500/10 dark:border-slate-700/70 dark:bg-slate-800/50 dark:ring-slate-700/20 dark:focus-within:border-teal-700/60 dark:focus-within:bg-slate-800 dark:focus-within:ring-teal-500/10 ${
            composerLocked ? "cursor-not-allowed opacity-75" : ""
          } ${
            activeTab === "note" ? "border-amber-200/80 bg-amber-50/30 focus-within:border-amber-300 dark:border-amber-800/50 dark:bg-amber-950/20 dark:focus-within:border-amber-700/60" : ""
          }`}
        >
          {slashMenu && activeTab === "reply" && (
            <div className="absolute bottom-full left-0 right-0 z-10 mb-1 max-h-48 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg shadow-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:shadow-black/30">
              <p className="border-b border-slate-100 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400 dark:border-slate-700/60 dark:text-slate-500">Saved replies</p>
              <ul id="saved-reply-slash-list" role="listbox" className="max-h-40 overflow-y-auto">
                {slashMenu.filtered.length === 0 ? (
                  <li className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">No match — keep typing or add one below.</li>
                ) : (
                  slashMenu.filtered.map((cr, i) => {
                    const active = i === slashMenu.activeIndex;
                    return (
                      <li key={cr.id} role="option" aria-selected={active} id={`saved-reply-option-${cr.id}`}>
                        <button
                          type="button"
                          ref={active ? slashActiveItemRef : undefined}
                          tabIndex={-1}
                          onMouseDown={(e) => e.preventDefault()}
                          onMouseEnter={() => setSlashMenu((prev) => (prev ? { ...prev, activeIndex: i } : prev))}
                          onClick={() => applySlashPick(cr)}
                          className={`flex w-full items-start gap-2 px-3 py-2 text-left ${
                            active ? "bg-teal-50 dark:bg-slate-800" : "hover:bg-slate-50 dark:hover:bg-slate-800/60"
                          }`}
                        >
                          <span className="shrink-0 rounded bg-slate-900 px-1 py-0.5 font-mono text-[10px] font-semibold text-white dark:bg-slate-700">
                            /{cr.shortcut}
                          </span>
                          <span className="min-w-0 text-xs font-medium text-slate-800 dark:text-slate-200">{cr.title}</span>
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          )}

          {isEmailTicket && activeTab === "reply" && emailSenders.length > 0 ? (
            <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-slate-200/60 pb-3 dark:border-slate-700/60">
              <label htmlFor="chat-from-sender" className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                From
              </label>
              <select
                id="chat-from-sender"
                value={selectedFromEmail}
                onChange={(e) => setSelectedFromEmail(e.target.value)}
                className="ui-input-subtle min-w-[220px] flex-1 text-xs"
                disabled={composerLocked || sendBusy || ticketSubmitBusy}
              >
                {emailSenders.map((sender) => (
                  <option key={sender.email} value={sender.email}>
                    {formatSenderLabel(sender)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <textarea
            ref={textareaRef}
            value={replyText}
            readOnly={composerLocked}
            disabled={composerLocked}
            onChange={(e) => {
              if (composerLocked) return;
              const v = e.target.value;
              const cursor = e.target.selectionStart;
              setReplyText(v);
              updateSlashMenu(v, cursor);
            }}
            onKeyUp={syncSlashMenuFromTextarea}
            onClick={syncSlashMenuFromTextarea}
            onSelect={syncSlashMenuFromTextarea}
            onPaste={handleComposerPaste}
            onKeyDown={(e) => {
              if (composerLocked) return;
              if (e.key === "Escape" && slashMenu) {
                e.preventDefault();
                setSlashMenu(null);
                return;
              }
              const slashOpen =
                slashMenu && activeTab === "reply" && slashMenu.filtered.length > 0;
              if (slashOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
                e.preventDefault();
                moveSlashActive(e.key === "ArrowDown" ? 1 : -1);
                return;
              }
              if (slashOpen && e.key === "Tab" && !e.shiftKey) {
                e.preventDefault();
                applySlashPick(slashMenu.filtered[slashMenu.activeIndex]);
                return;
              }
              if (e.key !== "Enter") return;
              if (e.shiftKey) return;
              if (e.isComposing || e.nativeEvent?.isComposing) return;

              e.preventDefault();
              if (slashOpen) {
                applySlashPick(slashMenu.filtered[slashMenu.activeIndex]);
                return;
              }
              void handleSend();
            }}
            placeholder={
              composerLocked
                ? "Conversation resolved — reopen to send messages."
                : activeTab === "reply"
                ? selectedTicket
                  ? "Draft your reply… Ticket actions below send (or update status only if empty). Type / for saved replies."
                  : "Write a thoughtful reply…  Enter to send, Shift+Enter for new line. Type / for saved replies."
                : "Private note — visible only to your team (Enter to send, Shift+Enter for new line)"
            }
            rows={3}
            className="w-full resize-none bg-transparent text-sm leading-relaxed text-slate-800 placeholder:text-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:text-slate-500 dark:text-slate-200 dark:placeholder:text-slate-500 dark:disabled:text-slate-500"
            aria-label={activeTab === "reply" ? "Reply message" : "Internal note"}
            aria-disabled={composerLocked}
            aria-autocomplete={activeTab === "reply" && !composerLocked ? "list" : undefined}
            aria-controls={slashMenu ? "saved-reply-slash-list" : undefined}
            aria-activedescendant={
              slashMenu?.filtered?.length
                ? `saved-reply-option-${slashMenu.filtered[slashMenu.activeIndex]?.id}`
                : undefined
            }
          />
          <div className="mt-3 flex items-center justify-between border-t border-slate-200/60 pt-3 dark:border-slate-700/60">
            <div className="flex flex-wrap items-center gap-0.5">
              <ComposerLinkControl
                disabled={composerLocked || sendBusy || ticketSubmitBusy}
                textareaRef={textareaRef}
                value={replyText}
                onChange={setReplyText}
              />
              <button
                type="button"
                title="AI suggested replies from conversation context"
                disabled={composerLocked || sendBusy || ticketSubmitBusy || aiSuggestLoading}
                onClick={() => void handleAiSuggest()}
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-2 text-[11px] font-semibold text-violet-900 shadow-sm transition hover:bg-violet-100 disabled:opacity-40 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-violet-300 dark:hover:bg-violet-900/50"
              >
                <i className="ri-sparkling-line text-base" aria-hidden />
                <span className="hidden sm:inline">{aiSuggestLoading ? "Analyzing…" : "AI suggest"}</span>
              </button>
              <button
                type="button"
                title="Improve spelling and basic English in the draft"
                disabled={composerLocked || !replyText.trim() || sendBusy || ticketSubmitBusy || aiProofLoading}
                onClick={() => void handleProofread()}
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 text-[11px] font-semibold text-slate-700 shadow-sm transition hover:border-teal-300 hover:text-teal-900 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-teal-600 dark:hover:text-teal-300"
              >
                <i className="ri-spell-check-line text-base" aria-hidden />
                <span className="hidden sm:inline">{aiProofLoading ? "Fixing…" : "Fix English"}</span>
              </button>
              <button
                type="button"
                title="Saved replies"
                onClick={() => savedRepliesRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })}
                className="ui-btn-icon !h-8 !w-8 text-slate-400 hover:text-teal-600"
              >
                <i className="ri-bookmark-line text-base" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden text-[11px] font-medium text-slate-400 sm:inline">
                Enter to send · Shift+Enter newline
              </span>
              <button
                type="button"
                onClick={() => void handleSend()}
                disabled={composerLocked || !attachController.canSend(replyText) || sendBusy || ticketSubmitBusy}
                className="ui-btn-primary !py-2 !text-xs disabled:opacity-40"
              >
                {selectedTicket ? "Send only" : "Send"}
                <i className="ri-send-plane-fill text-sm" aria-hidden />
              </button>
            </div>
          </div>
        </div>

        <div ref={savedRepliesRef}>
          {isHumanAgentInboxLive() && !activeWorkspaceId && (
            <p className="mb-2 text-xs font-medium text-amber-800 dark:text-amber-300">
              Select a workspace to use saved replies.
            </p>
          )}
          <CannedResponsesPanel
            user={conv.user}
            activeTab={activeTab}
            cannedResponses={cannedResponses}
            cannedLoading={cannedLoading}
            cannedError={cannedError}
            onRetryLoad={refreshCannedResponses}
            onCreate={createCannedResponse}
            onInsert={composerLocked ? undefined : insertAtCursor}
          />
        </div>
          </>
        )}
      </footer>
    </div>
  );
}
